import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {toast, ToastContainer} from 'react-toastify';
import { handleLogin } from "../../modules/authModules.ts";
import ParticlesAuth from "./ParticlesAuth.tsx";
import { Button, Card, CardBody, Col, Container, FormFeedback, Input, Label, Row, Form } from "reactstrap";

const Login = ({ onLogin }: { onLogin: () => void }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [userLogin, setUserLogin] = useState({ email: '', password: '' });
  const navigate = useNavigate();
  //const toast = useToast();
  const { username: email } = useParams();

  useEffect(() => {
    if (email) {
      setUserLogin(prev => ({ ...prev, email: email }));
    }
  }, [email]);

  useEffect(() => {
    const redirected = sessionStorage.getItem("redirect_handled");

    if (!redirected) {
      const isAuth = sessionStorage.getItem("isAuthenticated") === "true";
      const ump = sessionStorage.getItem("redirectedFromUMP") === "true";

      if (isAuth) {
        sessionStorage.setItem("redirect_handled", "true");
        navigate("/TemplateList");
        return;
      }

      if (ump) {
        sessionStorage.setItem("redirect_handled", "true");
        window.location.href = `${window.env.REACT_APP_UMP_REDIRECT_URL}/home`;
        return;
      }
    }
  }, []);

  const getTokens = (response: any) => {
    if (!response) return null;

    if ("access_token" in response) {
      return {
        access: response.access_token,
        refresh: response.refresh_token,
      };
    }

    if ("accessToken" in response) {
      return {
        access: response.accessToken,
        refresh: response.refreshToken,
      };
    }

    return null;
  };

  const storeTokens = (access: string, refresh?: string) => {
    sessionStorage.setItem("access_token", access);
    sessionStorage.setItem("refresh_token", refresh || "");
    sessionStorage.removeItem("redirectedFromUMP");
  };

  const decodeAndStoreUser = (token: string) => {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return;

      const payload = JSON.parse(atob(parts[1]));
      if (!payload?.user_id) return;

      sessionStorage.setItem("user_id", payload.user_id.toString());
      sessionStorage.setItem("tenant_id", payload.tenant_id?.toString());
      sessionStorage.setItem("role", "user");
    } catch (err) {
      console.error("Error decoding access token:", err);
    }
  };

  const handleRememberMe = (values: any) => {
    if (values.rememberMe) {
      sessionStorage.setItem("remembered_username", values.username);
      sessionStorage.setItem("remembered_password", values.password);
      sessionStorage.setItem("rememberMe", "true");
      return;
    }

    sessionStorage.removeItem("remembered_username");
    sessionStorage.removeItem("remembered_password");
    sessionStorage.removeItem("rememberMe");
  };

  const handleLoginSuccess = () => {
    toast.success("Login Successful! Welcome back!");
    sessionStorage.setItem("LoginToast", "True");
    onLogin();
    navigate("/TemplateList");
  };

  const getErrorMessage = (error: any) => {
    if (error?.response) {
      return (
        error.response.data?.detail ||
        JSON.stringify(error.response.data)
      );
    }
    return error?.message || "Something went wrong. Please try again.";
  };

  const loginFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: userLogin.email || sessionStorage.getItem("remembered_username") || "",
      password: userLogin.password || sessionStorage.getItem("remembered_password") || "",
      rememberMe: sessionStorage.getItem("rememberMe") === "true",
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .min(3, "Username must be at least 3 characters")
        .required("Username is required"),
      password: Yup.string().required("Password is required"),
      rememberMe: Yup.boolean(),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const response = await handleLogin({
          userName: values.username,
          password: values.password,
        });

        const tokens = getTokens(response);
        if (!tokens) return;

        storeTokens(tokens.access, tokens.refresh);
        decodeAndStoreUser(tokens.access);
        handleRememberMe(values);
        handleLoginSuccess();
      } catch (error: any) {
        toast.error(getErrorMessage(error));
      } finally {
        setSubmitting(false);
      }
    }
  });

  const signupFormik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      username: Yup.string()
        .min(3, "Username must be at least 3 characters")
        .required("Username is required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        toast.success("Account created successfully!");
        setIsLogin(true);
      } catch (error) {
        setErrors({ username: "Error creating account" });
        toast.error("Error creating account");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const currentFormik = isLogin ? loginFormik : signupFormik;

  // useEffect(() => {
  //  const debounce = setTimeout(() => { window.location.href = sessionStorage.getItem("isAuthenticated") === "true" ? "/TemplateList" : sessionStorage.getItem("redirectedFromUMP") === "true" ? `${window.env.REACT_APP_UMP_REDIRECT_URL}/home` : window.location.href},500)
  //  return () => clearTimeout(debounce);
  // },[sessionStorage.getItem("isAuthenticated")])

  return (
    (!localStorage.getItem("redirectedFromUMP")) && <ParticlesAuth>
      <div className='auth-page-content'>
        <Container>
          <ToastContainer position='top-center' />
          <Row className='justify-content-center'>
            <Col md={8} lg={6} xl={5}>
              <Card className='login-box'>
                <CardBody className='p-4'>
                  <div className='text-center mt-2'>
                    <h5 className='text-main-color'>Welcome Back !</h5>
                    <p className='text-muted'>Sign in to continue</p>
                  </div>
                  <div className='p-2 mt-4'>

                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        currentFormik.handleSubmit();
                        return false;
                      }}
                      action='#'
                    >
                      <div className='mb-3'>
                        <Label htmlFor='username' className='form-label'>
                          User ID
                        </Label>
                        <Input
                          name='username'
                          className='form-control'
                          placeholder='Enter User ID'
                          type='text'
                          onChange={currentFormik.handleChange}
                          onBlur={currentFormik.handleBlur}
                          value={currentFormik.values.username || ''}
                          invalid={
                            currentFormik.touched.username && currentFormik.errors.username
                              ? true
                              : false
                          }
                        />
                        {currentFormik.touched.username && currentFormik.errors.username ? (
                          <FormFeedback type='invalid'>
                            {currentFormik.errors.username}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className='mb-3'>
                        <Label htmlFor='password' className='form-label'>
                          Password
                        </Label>
                        <Input
                          name='password'
                          className='form-control'
                          placeholder='Enter your password'
                          type='password'
                          onChange={currentFormik.handleChange}
                          onBlur={currentFormik.handleBlur}
                          value={currentFormik.values.password || ''}
                          invalid={
                            currentFormik.touched.password && currentFormik.errors.password
                              ? true
                              : false
                          }
                        />
                        {currentFormik.touched.password && currentFormik.errors.password ? (
                          <FormFeedback type='invalid'>
                            {currentFormik.errors.password}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className='mt-1'>
                        <Button
                          className='btn bg-secondary w-100 mt-2'
                          type='submit'
                          disabled={currentFormik.isSubmitting}
                        >
                          {isLogin
                            ? currentFormik.isSubmitting
                              ? "Logging in..."
                              : "Login"
                            : currentFormik.isSubmitting
                              ? "Creating Account..."
                              : "Sign Up"}
                        </Button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default Login;
